import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ref, onValue } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { makeSyncRecorder } from '../../../integrations/rtdbMeter';

// One recorder per subscription: first delivery is a real download, the rest are updates.
// Attributed to the SESSION, not to whichever page happens to be open. This listener
// is started once by DashboardLayout and lives for the whole signed-in session, so
// blaming it on the current route made the Dashboard look like it used 2.5 GB and
// Quick Order 958 MB — neither of which had read anything.
const recordOrdersSync = makeSyncRecorder('/orders (whole tree)', 'session');

// The desk opens on TODAY's orders. A warehouse day is a calendar day, so this is IST
// — taking the browser's local date would show the wrong day for anyone not in IST.
const istToday = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const todayRange = () => ({ from: istToday(), to: istToday() });

const useOrderStore = create(devtools((set, get) => ({
    // Data State
    orders: [],
    isLoading: false,
    error: null,

    // Filter State
    filters: {
        status: [], // ['pending', 'shipped']
        paymentMode: [], // ['cod', 'prepaid']
        dateRange: todayRange(),      // default view = today
        keyword: '',
        specialFilter: [], // Pre-Shipment quick filters
        sourceFilter: '',  // where the order came from: website | storefront | parser | other
        summaryFilter: null, // Summary bar filter key
        showQuarantined: false, // show test/junk orders flagged excludeFromReports
    },

    // Pagination State
    pagination: {
        currentPage: 1,
        itemsPerPage: 10,
    },

    // Selection State
    selectedOrderIds: new Set(),

    // RTO risk per order key ({ score, label, reasons, advice, adviceText, fetchedAt }),
    // fetched in batches from the proxy for the COD orders on screen (useRtoRisks).
    rtoRisks: {},
    mergeRtoRisks: (risks) => set((state) => ({ rtoRisks: { ...state.rtoRisks, ...risks } })),

    // Actions
    setOrders: (orders) => set({ orders, isLoading: false }),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),

    setFilter: (key, value) => set((state) => ({
        filters: { ...state.filters, [key]: value },
        pagination: { ...state.pagination, currentPage: 1 } // Reset page on filter change
    })),

    resetFilters: () => set({
        filters: {
            status: [],
            paymentMode: [],
            dateRange: todayRange(),  // "clear filters" returns to today, not all time
            keyword: '',
            specialFilter: [],
            sourceFilter: '',
            summaryFilter: null,
        },
        pagination: { currentPage: 1, itemsPerPage: 10 }
    }),

    setPage: (page) => set((state) => ({
        pagination: { ...state.pagination, currentPage: page }
    })),

    setItemsPerPage: (limit) => set((state) => ({
        pagination: { ...state.pagination, itemsPerPage: limit, currentPage: 1 }
    })),

    // Selection Actions
    toggleSelection: (orderId) => set((state) => {
        const newSelection = new Set(state.selectedOrderIds);
        if (newSelection.has(orderId)) {
            newSelection.delete(orderId);
        } else {
            newSelection.add(orderId);
        }
        return { selectedOrderIds: newSelection };
    }),

    selectAll: (orderIds) => set({ selectedOrderIds: new Set(orderIds) }),
    clearSelection: () => set({ selectedOrderIds: new Set() }),

    // --- Single shared realtime subscription (consolidates duplicate listeners) ---
    // Reference-counted: the first consumer opens ONE onValue('orders') listener,
    // every consumer reads from `orders`, and the listener is torn down when the
    // last consumer leaves. Consumers call subscribeOrders() in an effect and
    // invoke the returned unsubscribe on cleanup, instead of each opening their
    // own onValue listener (which previously caused 2+ overlapping subscriptions).
    _subCount: 0,
    _unsub: null,

    subscribeOrders: () => {
        const state = get();
        set({ _subCount: state._subCount + 1 });

        if (!state._unsub) {
            set({ isLoading: true });
            const unsub = onValue(
                ref(db, 'orders'),
                (snapshot) => {
                    // Measure what this listener actually PULLS, not what it hands back.
                    // onValue re-delivers the whole tree on every change, but only the
                    // changed order crosses the network — counting the full snapshot
                    // each time reported 2.5 GB for a few MB of real traffic.
                    recordOrdersSync(snapshot.val());
                    if (snapshot.exists()) {
                        const data = snapshot.val();
                        const ordersList = Object.keys(data).map((key) => ({
                            ...data[key],
                            firebaseKey: key,
                            id: data[key].id || key,
                        })).reverse();
                        set({ orders: ordersList, isLoading: false });
                    } else {
                        set({ orders: [], isLoading: false });
                    }
                },
                (err) => set({ error: err.message, isLoading: false })
            );
            set({ _unsub: unsub });
        }

        return () => {
            const s = get();
            const next = Math.max(0, s._subCount - 1);
            if (next === 0 && s._unsub) {
                s._unsub();
                set({ _unsub: null, _subCount: 0 });
            } else {
                set({ _subCount: next });
            }
        };
    },

    // Computed Helpers (Can be used inside components via selectors)
    // e.g. const selectedCount = useOrderStore(s => s.selectedOrderIds.size);
})));

export default useOrderStore;
